import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import { AuthState, UserBalance } from "@/types";

const initialState: AuthState = {
  isLoggedIn: false,
  user: null,
  token: null,
  balance: null,
  overdrawn: 0,
  chatRoomOpen: false,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    login(state, action: PayloadAction<{ user: AuthState["user"]; token: string }>) {
      state.isLoggedIn = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
    },
    logout(state) {
      state.isLoggedIn = false;
      state.user = null;
      state.token = null;
      state.overdrawn = 0;
    },
    updateUser(state, action: PayloadAction<Partial<AuthState["user"]>>) {
      if (state.isLoggedIn && state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
    updateBalance(state, action: PayloadAction<Partial<UserBalance>>) {
      if (!state.isLoggedIn) return;

      const prev = state.balance;
      const currency = action.payload?.currency || prev?.currency || state.user?.currency || "";
      const balance = action.payload?.balance ?? prev?.balance ?? 0;
      const locked = action.payload?.locked ?? prev?.locked ?? 0;
      const bonus = action.payload?.bonus ?? prev?.bonus ?? 0;

      // A socket push does not name the row, so the last known one stands.
      const baseCurrency = action.payload?.baseCurrency ?? prev?.baseCurrency;

      // Only GET /api/balance carries the quote; a push keeps the last one.
      const show = action.payload && "show" in action.payload ? action.payload.show : prev?.show;

      state.balance = { currency, balance, locked, bonus, baseCurrency, show };
      // The server's own figure, so nothing is owed against it any more.
      state.overdrawn = 0;

      if (state.user && currency) {
        // Deliberately not writing `state.user.currency` here: a balance
        // response is not the authority on the user's currency, and letting it
        // win would undo the value `updateUser` just set after a switch.
        state.user.balances = { ...state.user.balances, [currency]: balance };
      }
    },

    /**
     * Moves the shown balance by a delta, carrying whatever it cannot cover.
     *
     * A stake is taken optimistically on the click, so a bet larger than the
     * balance asks for more than there is. Clamping at zero and forgetting the
     * difference is what made a refused bet hand back more than it took - stake
     * 10 against a balance of 6 and the deduction stopped at 0, then the refund
     * added the full 10 back and showed 10. The shortfall is held in
     * `overdrawn` instead, so the deltas still net out exactly.
     */
    adjustBalance(state, action: PayloadAction<number>) {
      if (!state.isLoggedIn || !state.balance || !action.payload) return;

      const round8 = (value: number) => Math.round(value * 1e8) / 1e8;

      // `?? 0` for a session persisted before this field existed.
      const next = round8(state.balance.balance - (state.overdrawn ?? 0) + action.payload);
      const shown = Math.max(0, next);

      state.overdrawn = round8(shown - next);
      state.balance = { ...state.balance, balance: shown };

      if (state.user && state.balance.currency) {
        state.user.balances = { ...state.user.balances, [state.balance.currency]: shown };
      }
    },
    setChatRoom(state, action: PayloadAction<boolean>) {
      state.chatRoomOpen = action.payload;
    },
  },
});

export default authSlice.reducer;

export const { login, logout, updateUser, updateBalance, adjustBalance, setChatRoom } =
  authSlice.actions;
